import React, { useState } from 'react';
import {
  Document,
  Page,
  View,
  Text,
  Image,
  StyleSheet,
  pdf,
} from '@react-pdf/renderer';
import Button from '../../ui/Button';

export type PdfSize = 'table_tent' | 'half_sheet' | 'full_sheet' | 'sticker_sheet';

interface PdfQrSheetProps {
  businessName: string;
  ssid: string;
  qrDataUrl: string;
  slug: string;
}

const PDF_SIZES: Record<PdfSize, { label: string; width: number; height: number; qrSize: number }> = {
  table_tent: { label: 'Table Tent (4x6")', width: 288, height: 432, qrSize: 108 },
  half_sheet: { label: 'Half Sheet (5.5x8.5")', width: 396, height: 612, qrSize: 180 },
  full_sheet: { label: 'Full Sheet (8.5x11")', width: 612, height: 792, qrSize: 252 },
  sticker_sheet: { label: 'Sticker Sheet (8.5x11")', width: 612, height: 792, qrSize: 108 },
};

const colors = {
  coffeeDark: '#3E2723',
  coffeeMedium: '#6D4C41',
  coffeeLight: '#D7CCC8',
  coffeeBg: '#EFEBE9',
  white: '#FFFFFF',
  accent: '#795548',
};

const baseStyles = StyleSheet.create({
  page: {
    backgroundColor: colors.coffeeBg,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  brandText: {
    fontSize: 10,
    color: colors.coffeeMedium,
    marginBottom: 8,
    fontFamily: 'Helvetica',
  },
  businessName: {
    fontSize: 22,
    color: colors.coffeeDark,
    fontFamily: 'Helvetica-Bold',
    marginBottom: 12,
    textAlign: 'center',
  },
  qrImage: {
    marginVertical: 12,
  },
  ctaText: {
    fontSize: 16,
    color: colors.coffeeDark,
    fontFamily: 'Helvetica-Bold',
    marginTop: 12,
    textAlign: 'center',
  },
  ssidLabel: {
    fontSize: 10,
    color: colors.coffeeMedium,
    marginTop: 8,
  },
  ssidValue: {
    fontSize: 14,
    color: colors.coffeeDark,
    fontFamily: 'Helvetica-Bold',
    marginTop: 2,
  },
  urlText: {
    fontSize: 8,
    color: colors.accent,
    marginTop: 10,
  },
});

/** Single QR sheet (table tent, half sheet, full sheet) */
const SingleQrPage: React.FC<{
  size: PdfSize;
  businessName: string;
  ssid: string;
  qrDataUrl: string;
  slug: string;
}> = ({ size, businessName, ssid, qrDataUrl, slug }) => {
  const cfg = PDF_SIZES[size];
  const nameFontSize = size === 'table_tent' ? 16 : size === 'half_sheet' ? 20 : 26;
  const ctaFontSize = size === 'table_tent' ? 12 : size === 'half_sheet' ? 16 : 20;

  return (
    <Page size={[cfg.width, cfg.height]} style={baseStyles.page}>
      <Text style={baseStyles.brandText}>WhoBrew</Text>
      <Text style={[baseStyles.businessName, { fontSize: nameFontSize }]}>
        {businessName}
      </Text>
      <Image src={qrDataUrl} style={[baseStyles.qrImage, { width: cfg.qrSize, height: cfg.qrSize }]} />
      <Text style={[baseStyles.ctaText, { fontSize: ctaFontSize }]}>
        Scan for Free Wi-Fi
      </Text>
      <Text style={baseStyles.ssidLabel}>Network:</Text>
      <Text style={baseStyles.ssidValue}>{ssid}</Text>
      <Text style={baseStyles.urlText}>whobrew.us/v/{slug}</Text>
    </Page>
  );
};

/** Sticker sheet: grid of QR codes with cut guides */
const StickerSheetPage: React.FC<{
  businessName: string;
  ssid: string;
  qrDataUrl: string;
  slug: string;
}> = ({ businessName, ssid, qrDataUrl, slug }) => {
  const cfg = PDF_SIZES.sticker_sheet;
  // 2x2" stickers = 144x144 points. Fit 3 cols x 4 rows = 12 stickers
  const stickerSize = 144;
  const cols = 3;
  const rows = 4;
  const margin = (cfg.width - cols * stickerSize) / 2;

  return (
    <Page size={[cfg.width, cfg.height]} style={{ padding: 0, backgroundColor: colors.white }}>
      <View style={{ marginLeft: margin, marginTop: margin }}>
        {Array.from({ length: rows }).map((_, row) => (
          <View key={row} style={{ flexDirection: 'row' }}>
            {Array.from({ length: cols }).map((_, col) => (
              <View
                key={col}
                style={{
                  width: stickerSize,
                  height: stickerSize,
                  border: `0.5pt dashed ${colors.coffeeLight}`,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 4,
                }}
              >
                <Text style={{ fontSize: 5, color: colors.coffeeMedium, marginBottom: 2 }}>
                  WhoBrew
                </Text>
                <Text style={{ fontSize: 7, color: colors.coffeeDark, fontFamily: 'Helvetica-Bold', marginBottom: 3, textAlign: 'center' }}>
                  {businessName}
                </Text>
                <Image src={qrDataUrl} style={{ width: cfg.qrSize, height: cfg.qrSize }} />
                <Text style={{ fontSize: 6, color: colors.coffeeDark, fontFamily: 'Helvetica-Bold', marginTop: 2 }}>
                  Scan for Free Wi-Fi
                </Text>
                <Text style={{ fontSize: 5, color: colors.coffeeMedium, marginTop: 1 }}>
                  {ssid}
                </Text>
              </View>
            ))}
          </View>
        ))}
      </View>
    </Page>
  );
};

/** Create the PDF document for a given size */
export const QrPdfDocument: React.FC<PdfQrSheetProps & { size: PdfSize }> = ({
  size,
  businessName,
  ssid,
  qrDataUrl,
  slug,
}) => {
  return (
    <Document>
      {size === 'sticker_sheet' ? (
        <StickerSheetPage
          businessName={businessName}
          ssid={ssid}
          qrDataUrl={qrDataUrl}
          slug={slug}
        />
      ) : (
        <SingleQrPage
          size={size}
          businessName={businessName}
          ssid={ssid}
          qrDataUrl={qrDataUrl}
          slug={slug}
        />
      )}
    </Document>
  );
};

/** Generate and download a PDF for the given size */
export async function downloadQrPdf(
  size: PdfSize,
  businessName: string,
  ssid: string,
  qrDataUrl: string,
  slug: string
): Promise<void> {
  const doc = (
    <QrPdfDocument
      size={size}
      businessName={businessName}
      ssid={ssid}
      qrDataUrl={qrDataUrl}
      slug={slug}
    />
  );

  const blob = await pdf(doc).toBlob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const safeName = businessName.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
  a.href = url;
  a.download = `${safeName}-${size}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
}

/** Helper component: download buttons for all 4 sizes */
const PdfDownloadButtons: React.FC<{
  businessName: string;
  ssid: string;
  qrDataUrl: string;
  slug: string;
}> = ({ businessName, ssid, qrDataUrl, slug }) => {
  const [generating, setGenerating] = useState<PdfSize | null>(null);

  const handleDownload = async (size: PdfSize) => {
    setGenerating(size);
    try {
      await downloadQrPdf(size, businessName, ssid, qrDataUrl, slug);
    } catch (err) {
      console.error('PDF generation failed:', err);
    } finally {
      setGenerating(null);
    }
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {(Object.entries(PDF_SIZES) as [PdfSize, typeof PDF_SIZES[PdfSize]][]).map(
        ([key, cfg]) => (
          <Button
            key={key}
            variant="primary"
            onClick={() => handleDownload(key)}
            disabled={generating !== null}
            loading={generating === key}
            aria-label={`Download ${cfg.label} PDF`}
          >
            {generating === key ? 'Generating...' : cfg.label}
          </Button>
        )
      )}
    </div>
  );
};

export { PDF_SIZES };
export default PdfDownloadButtons;
